import React from 'react';
import {
  Flex,
  Box,
  FormControl,
  FormLabel,
  FormErrorMessage,
  RadioGroup,
  Radio,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Controller, Control, FieldValues } from 'react-hook-form';
import Subtitle from '../Subtitle/Subtitle';

interface Option {
  value: string;
  label: string;
}

type ActivityCode = 'P' | 'A' | 'S';

interface RadioComponentProps<T extends FieldValues> {
  name: string;
  control: Control<T>;
  label: string;
  options: Option[];
  rules?: object;
  onRadioClick: (value: any) => void;
  error?: any;

  variant?: 'default' | 'extraQuestion';
  activityCode?: ActivityCode;
  activityName?: string;
}

const RadioComponent = <T extends FieldValues>({
  name,
  control,
  label,
  options,
  rules,
  onRadioClick,
  error,
  variant = 'default',
  activityCode,
  activityName,
}: RadioComponentProps<T>) => {
  const renderSubtitle = () => {
    if (variant === 'extraQuestion' && activityCode && activityName) {
      const prefixMap = {
        P: 'Profissão',
        A: 'Actividade',
        S: 'Desporto',
      };

      const prefix = prefixMap[activityCode];

      return prefix ? `${prefix}: ${activityName}` : '';
    }
    return null;
  };

  return (
    <>
      {variant === 'extraQuestion' && (
        <Subtitle text={renderSubtitle() ?? ''} format={'10'} />
      )}

      <Flex
        flexDir={['column', 'row']}
        justify={['flex-start', 'space-between']}
        align={['flex-start', 'center']}
        mb="15px"
      >
        <FormLabel
          fontWeight={400}
          p={['0 0 5px 0', '0']}
          margin={['unset', '0 10px 0 0']}
          w="100%"
          maxW={['100%', '500px']}
          color="brand.fontColors.primary"
        >
          {label}
        </FormLabel>
        <Box width={['100%', 'calc(100% - 220px)']}>
          <Controller
            name={name}
            control={control}
            rules={rules}
            defaultValue={null}
            render={props => (
              <FormControl isInvalid={!!error}>
                <RadioGroup
                  value={props.value}
                  onChange={val => {
                    props.onChange(val);

                    if (variant === 'default') {
                      onRadioClick(val === 'true');
                    } else {
                      onRadioClick(val);
                    }
                  }}
                >
                  <Stack direction="row" gap="30px">
                    {options.map(option => (
                      <Radio
                        key={`${name}-${option.value}`}
                        size="lg"
                        id={`${name}-${option.value}`}
                        value={option.value}
                      >
                        <Text fontSize="md" color="brand.fontColors.primary">
                          {option.label}
                        </Text>
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
                <FormErrorMessage>{error?.message}</FormErrorMessage>
              </FormControl>
            )}
          />
        </Box>
      </Flex>
    </>
  );
};

export default RadioComponent;
